import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../services/auth/auth.service';
import Icon from '../Icons/Icon';
import LogoWithText from './Logo/LogoWithText';
import LogoWithTextMobile from './Logo/LogoWithTextMobile';
import NavAccordion from './NavAccordion/NavAccordion';
import styles from './Navigation.module.css';
import { useTranslation } from 'react-i18next';
import { useSTKContext } from '@/context/context';
import { useQueryClient } from '@tanstack/react-query';
import { useBundleAddOnsFeatureFlag } from '@/services/featureFlags/featureFlags.service';

interface NavigationProps {
  isAdmin?: boolean;
}

const Navigation = ({ isAdmin }: NavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();

  const handleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  // we don't want to rerun this effect when isOpen changes, only when we change location
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // close the mobile nav whenever we navigate to a new page
    if (isOpen) setIsOpen(false);
  }, [location.pathname]);

  const isBundleAddOnsEnabled = useBundleAddOnsFeatureFlag();

  const adminLinks = (
    <>
      <NavAccordion
        sublinks={[
          { children: t('nav.bundlesLibrary'), to: '/bundles/library' },
          { children: t('nav.bundleUpcharges'), to: '/bundles/upcharges' },
          ...(isBundleAddOnsEnabled ? [{ children: t('nav.bundleAddOns'), to: '/bundles/add-ons' }] : []),
        ]}
        title={t('nav.bundles')}
        defaultOpen={location.pathname.includes('bundles')}
      />
      <NavAccordion
        sublinks={[
          { children: t('nav.promotionsLibrary'), to: '/promotions/active' },
          { children: t('nav.manageCERs'), to: '/promotions/cer' },
        ]}
        title={t('nav.promotions')}
      />
      <NavAccordion
        sublinks={[
          {
            children: t('nav.menuNationalProducts'),
            to: '/nationalproducts',
          },
          {
            children: t('nav.menuNationalModifiers'),
            to: '/nationalmodifiers',
          },
        ]}
        title={t('nav.menu')}
      />
      <li>
        <NavLink title={t('nav.userManagement')} to="users">
          {t('nav.userManagement')}
        </NavLink>
      </li>
      <li>
        <NavLink title={t('nav.franchiseePortal')} to="/franchiseeportal">
          {t('nav.franchiseePortal')}
        </NavLink>
      </li>
      <li>
        <NavLink title={t('nav.storeOnboarding')} to="/storeonboarding">
          {t('nav.storeOnboarding')}
        </NavLink>
      </li>
      <li>
        <NavLink title={t('nav.extractQueue')} to="/submissions">
          {t('nav.extractQueue')}
        </NavLink>
      </li>
      <li>
        <NavLink title={t('nav.toolkitFreeze')} to="/freezes">
          {t('nav.toolkitFreeze')}
        </NavLink>
      </li>
      <li>
        <NavLink
          title={t('nav.accountLogOut')}
          to="/logout"
          onClick={(event) => {
            event.preventDefault();
            logout(apiClient, queryClient);
          }}
        >
          {t('nav.accountLogOut')}
        </NavLink>
      </li>
    </>
  );

  const userLinks = (
    <>
      <NavAccordion
        sublinks={[
          { children: t('nav.bundlesActive'), to: '/bundles/active' },
          { children: t('nav.bundlesLibrary'), to: '/bundles/library' },
          { children: t('nav.bundleUpcharges'), to: '/bundles/upcharges' },
          ...(isBundleAddOnsEnabled ? [{ children: t('nav.bundleAddOns'), to: '/bundles/add-ons' }] : []),
        ]}
        title={t('nav.bundles')}
        defaultOpen={location.pathname.includes('bundles')}
      />
      <li>
        <NavLink title={t('nav.promotions')} to="/promotions/active">
          {t('nav.promotions')}
        </NavLink>
      </li>
      <NavAccordion
        sublinks={[
          { children: t('nav.product'), to: '/menus' },
          { children: t('nav.modifier'), to: '/modifiers' },
        ]}
        title={t('nav.menu')}
        defaultOpen={location.pathname.includes('menu')}
      />
      <li>
        <NavLink title={t('nav.yourSubmissions')} to="/submissions">
          {t('nav.yourSubmissions')}
        </NavLink>
      </li>
      <li>
        <NavLink title={t('nav.calendar')} to="/calendar">
          {t('nav.calendar')}
        </NavLink>
      </li>
      <NavAccordion
        sublinks={[
          { children: t('nav.accountProfile'), to: '/account/profile' },
          { children: t('nav.accountStores'), to: '/account/stores' },
          { children: t('nav.accountOrg'), to: '/account/org' },
          {
            children: t('nav.accountLogOut'),
            to: '/logout',
            onClick: (event) => {
              event.preventDefault();
              logout(apiClient, queryClient);
            },
          },
        ]}
        defaultOpen={location.pathname.includes('account')}
        title={t('nav.account')}
      />
      {/* <NavLink title="Help" to="/help">
        Help
      </NavLink> */}
    </>
  );

  return (
    <nav className={classNames(styles.navigation, { [styles.isOpen]: isOpen })}>
      <a href="#main" className={styles.skipNav}>
        {t('nav.skipNavigation')}
      </a>
      <button
        className={styles.logoContainer}
        onClick={() => navigate('/')}
        role="link"
        data-testid="logo"
        type="button"
      >
        <LogoWithText />
      </button>
      <div className={styles.buttons}>
        <button
          className={styles.mobileLogoContainer}
          onClick={() => navigate('/')}
          role="link"
          data-testid="logo"
          type="button"
        >
          <LogoWithTextMobile />
        </button>
        <button
          className={styles.hamburger}
          onClick={handleIsOpen}
          data-testid="hamburger"
          type="button"
          aria-label="open menu"
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : 'false'}
        >
          <Icon iconName={isOpen ? 'X' : 'Menu'} />
        </button>
      </div>
      <ul
        className={classNames(styles.navLinks, { [styles.isOpen]: isOpen })}
        data-testid={isAdmin ? 'adminLinks' : 'franchiseeLinks'}
      >
        {isAdmin && sessionStorage.getItem('aliasInfo') === null ? adminLinks : userLinks}
      </ul>
    </nav>
  );
};

export default Navigation;
